import { colors } from '@/constants';
import styled from 'styled-components';
import { pxToPdf } from '@/shared/commonFunctions';
import { RiShareForward2Fill } from 'react-icons/ri';

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;

/* ------------------------------- */
/* SHARED */
/* ------------------------------- */

export const RowGrid = styled.div<{ grids: number }>`
  display: grid;
  grid-template-columns: ${({ grids }) => `repeat(${grids}, 1fr)`};
`;

export const RowFlex = styled.div`
  gap: 10px;
  width: 100%;
  align-items: center;
  display: inline-flex;
  justify-content: space-between;
`;

export const Row = styled.div`
  display: grid;
  gap: 10px;
  align-items: start;
  grid-template-columns: auto 1fr;
`;

export const Col = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const CheckStep = styled.div<{ $checked?: boolean }>`
  border-radius: 50%;
  margin: ${({ $checked }) => !$checked ? pxToPdf(3) : 0} 0px;
  width: ${({ $checked }) => $checked ? pxToPdf(10) : pxToPdf(5)};
  height: ${({ $checked }) => $checked ? pxToPdf(10) : pxToPdf(5)};
  background-color: ${({ $checked }) => ($checked ? '#27AE61' : '#fff')};
  border: 1px solid ${({ $checked }) => ($checked ? '#51D88B' : '#999999')};
`;

export const LineProgress = styled.div<{ $checked?: boolean }>`
  top: 50%;
  left: 0px;
  width: 100%;
  position: absolute;
  height: ${pxToPdf(3)};
  transform: translateY(-50%);
  background-color: ${({ $checked }) => ($checked ? '#27AE61' : '#fff')};
`;

export const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-top: ${pxToPdf(10)};
  margin-bottom: ${pxToPdf(10)};
  background-color: #ddd;
`;

/* ------------------------------- */
/* BUTTON */
/* ------------------------------- */

export const ShareMarketOrder = styled.button`
  gap: 10px;
  width: 100%;
  border: none;
  padding: 14px;
  cursor: pointer;
  font-weight: 700;
  border-radius: 6px;
  margin-bottom: 16px;
  align-items: center;
  display: inline-flex;
  justify-content: center;
  color: ${colors.brand.white};
  background-color: ${colors.green};
  ${({ disabled }) => disabled && 'opacity: 0.5;'}
`;

export const ShareMarketOrderIcon = styled(RiShareForward2Fill)`
  width: 24px;
  height: 24px;
  color: ${colors.brand.white};
`;

/* PDF CONTENT */
export const Logo = styled.img`
  width: 60%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const Content = styled.div`
  width: 0;
  height: 0;
  overflow: hidden;
  position: absolute;
`;

export const PDFPage = styled.div`
  position: relative;
  width: ${PAGE_WIDTH}mm;
  padding: ${pxToPdf(30)} ${pxToPdf(20)} ${pxToPdf(65)} ${pxToPdf(20)};
  height: ${PAGE_HEIGHT}mm;
`;

export const PDFBody = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #ccc;
`;

export const AlertBox = styled.div`
  width: 100%;
  border: 1px solid #F44336;
  padding: 0 ${pxToPdf(15)};
  background-color: #FFF6F8;
  border-radius: 5px;
  margin-bottom: ${pxToPdf(10)};
`;

export const BoxPadding = styled.div`
  width: 100%;
  padding: 0 ${pxToPdf(15)};
`;

export const THead = styled.thead`
  width: 100%;
`;

export const TBody = styled.tbody`
  width: 100%;
`;

export const TRow = styled.tr<{ $bordered?: boolean, $minHeight?: boolean }>`
  width: 100%;
  border-top: ${({ $bordered }) => ($bordered ? '1px solid #E9E8E7' : 'none')};
  td {
    min-height: ${({ $minHeight }) => $minHeight ? '48px' : 'auto'};
    padding-top: ${({ $bordered }) => ($bordered ? pxToPdf(6) : 0)};
    padding-bottom: ${({ $bordered }) => ($bordered ? pxToPdf(4) : 0)};
  }
`;

export const THeadCell = styled.th`
  font-size: 9px;
  margin-bottom: ${pxToPdf(10)};
  text-align: left;
  font-weight: 500;
  color: ${colors.brand.grafite};
  font-size: 9px;
`;

export const TCell = styled.td`
  font-size: 9px;
  text-align: left;
`;

export const Box = styled.div<{ padding?: number }>`
  background-color: #eee;
  border-radius: 5px;
  width: fit-content;
  padding: 5px ${({ padding }) => padding ?? 5}px;
`;

export const Table = styled.table`
  width: 100%;
`;

export const ContentTable = styled.div`
  width: 100%;
  padding: 0 ${pxToPdf(14)};
  overflow: hidden;
`;

/* ------------------------------- */
/* SHARE ORDER PAGE */
/* ------------------------------- */

export const OrderPageRow = styled.div<{ position: 'top' | 'bottom'; show?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: calc(100% - ${pxToPdf(40)});
  opacity: ${({ show }) => (show ? 1 : 0)};
  ${({ position }) => position === 'top' && 'top: 15px'};
  position: ${({ show }) => (show ? 'absolute' : 'relative')};
  ${({ position }) => position === 'bottom' && 'bottom: 20px'};
`;

export const Text = styled.span<{ position?: 'center' | 'flex-start' | 'flex-end' }>`
  font-size: 10px;
  font-weight: 400;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: ${({ position }) => position};
  width: 100%;
  gap: 5px;
`;

export const BoldText = styled.span<{ position?: 'center' | 'flex-start' | 'flex-end' }>`
  font-size: 10px;
  color: #37322D;
  display: flex;
  align-items: center;
  justify-content: ${({ position }) => position};
  gap: 5px;
  font-weight: 700;
`;

/* ------------------------------- */
/* ORDER HEADER */
/* ------------------------------- */

export const OrderHeader = styled.div`
  padding: ${pxToPdf(12)} ${pxToPdf(14)};
  border-bottom: 1px solid #f0f0ee;
  background-color: #f0f0ee;
  margin-bottom: ${pxToPdf(20)};
`;

export const BgIcon = styled.div`
  display: flex;
  border-radius: 50%;
  align-items: center;
  width: 25px;
  height: 25px;
  justify-content: center;
  background-color: #37322d;
`;